import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  CalendarToday as DateIcon,
  Store as WarehouseIcon,
  Numbers as CountIcon
} from '@mui/icons-material';
import { formatDate, formatNumber } from '../../../services/inventoryService';

/**
 * ImportReviewStep - Step 3: Final review before import
 * Shows summary of what will be imported, duplicate checks, and final confirmation
 */
function ImportReviewStep({
  parsedData,
  mapping,
  duplicateCheck,
  validationSummary,
  loading,
  error,
  importProgress,
  importResult
}) {
  const csvRows = parsedData?.rows || [];

  // Group rows by warehouse and date for summary
  const importSummary = useMemo(() => {
    const warehouses = {};
    const dates = new Set();
    const skus = new Set();
    let totalQuantity = 0;

    csvRows.forEach(row => {
      const warehouseCode = row[mapping.warehouse_code]?.toString().toUpperCase().trim();
      const countDate = row[mapping.count_date];
      const sku = row[mapping.sku];
      const qty = parseFloat(row[mapping.counted_quantity]) || 0;

      if (warehouseCode) {
        if (!warehouses[warehouseCode]) {
          warehouses[warehouseCode] = { count: 0, quantity: 0 };
        }
        warehouses[warehouseCode].count++;
        warehouses[warehouseCode].quantity += qty;
      }

      if (countDate) dates.add(countDate);
      if (sku) skus.add(sku);
      totalQuantity += qty;
    });

    return {
      warehouses,
      uniqueDates: Array.from(dates),
      uniqueSkus: skus.size,
      totalRows: csvRows.length,
      totalQuantity
    };
  }, [csvRows, mapping]);

  // Calculate validation stats
  const validStats = useMemo(() => {
    if (!validationSummary) {
      return { valid: csvRows.length, errors: 0, warnings: 0 };
    }
    return validationSummary;
  }, [validationSummary, csvRows.length]);

  // Show import progress
  if (importProgress) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Importing Physical Counts...
        </Typography>
        <Box sx={{ maxWidth: 400, mx: 'auto', mt: 3 }}>
          <LinearProgress
            variant="determinate"
            value={importProgress.percentage || 0}
            sx={{ height: 8, borderRadius: 1 }}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {importProgress.current || 0} of {importProgress.total || csvRows.length} rows processed
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show import results
  if (importResult) {
    const isSuccess = importResult.success;
    const isPartial = isSuccess && importResult.refused?.length > 0;   // DT-901: some groups were refused
    return (
      <Box>
        <Alert
          severity={isPartial ? 'warning' : isSuccess ? 'success' : 'error'}
          icon={isPartial ? undefined : isSuccess ? <CheckIcon /> : <ErrorIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2">
            {isPartial ? 'Import Partly Completed' : isSuccess ? 'Import Completed Successfully!' : 'Import Failed'}
          </Typography>
          <Typography variant="body2">
            {importResult.message}
          </Typography>
        </Alert>

        {isSuccess && importResult.summary && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import Summary
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CountIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary={`${formatNumber(importResult.summary.countsCreated || 0)} physical count(s) created`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InventoryIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary={`${formatNumber(importResult.summary.itemsCreated || 0)} count item(s) recorded`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><WarehouseIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary={`${formatNumber(importResult.summary.adjustmentsCreated || 0)} inventory adjustment(s) generated`}
                />
              </ListItem>
            </List>

            {importResult.summary.skipped > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {formatNumber(importResult.summary.skipped)} row(s) were skipped due to validation errors
              </Alert>
            )}

            {importResult.refused?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {formatNumber(importResult.refused.length)} count(s) were not imported:
                <List dense disablePadding>
                  {importResult.refused.slice(0, 10).map((line, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText primary={line} />
                    </ListItem>
                  ))}
                  {importResult.refused.length > 10 && (
                    <ListItem disableGutters>
                      <ListItemText primary={`... and ${formatNumber(importResult.refused.length - 10)} more`} />
                    </ListItem>
                  )}
                </List>
              </Alert>
            )}
          </Paper>
        )}

        {!isSuccess && importResult.errors && (
          <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Errors Encountered
            </Typography>
            <List dense>
              {importResult.errors.slice(0, 10).map((err, i) => (
                <ListItem key={i}>
                  <ListItemIcon><ErrorIcon color="error" fontSize="small" /></ListItemIcon>
                  <ListItemText primary={err} />
                </ListItem>
              ))}
              {importResult.errors.length > 10 && (
                <ListItem>
                  <ListItemText
                    primary={`... and ${importResult.errors.length - 10} more errors`}
                    secondary="Please review your CSV file and try again"
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography>Checking for duplicates...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 3: Review & Confirm Import
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Review the import summary below before confirming. This will create physical count records and inventory adjustments.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Validation Status */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {validStats.errors === 0 ? (
            <CheckIcon color="success" />
          ) : (
            <ErrorIcon color="error" />
          )}
          <Typography variant="subtitle2">
            Validation Status
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<CheckIcon />}
            label={`${formatNumber(validStats.valid)} Valid Rows`}
            color="success"
            variant="outlined"
          />
          {validStats.errors > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${formatNumber(validStats.errors)} Rows with Errors`}
              color="error"
              variant="outlined"
            />
          )}
          {validStats.warnings > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${formatNumber(validStats.warnings)} Warnings`}
              color="warning"
              variant="outlined"
            />
          )}
        </Box>

        {validStats.errors > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Rows with errors will be skipped during import. Go back to fix them or proceed with valid rows only.
          </Alert>
        )}
      </Paper>

      {/* Duplicate Check */}
      {duplicateCheck && duplicateCheck.hasDuplicates && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Potential Duplicates Detected
          </Typography>
          <Typography variant="body2">
            Found {duplicateCheck.duplicates?.length || 0} existing count(s) that may overlap with this import.
            Importing will create new counts - it will NOT overwrite existing data.
          </Typography>
          {duplicateCheck.duplicates?.slice(0, 3).map((dup, i) => (
            <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>
              • {dup.warehouse_code} on {formatDate(dup.count_date)}: {formatNumber(dup.total_items)} items
            </Typography>
          ))}
        </Alert>
      )}

      {/* Import Summary */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Import Summary
        </Typography>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 3 }}>
          <Box>
            <Typography variant="h4" color="primary">
              {formatNumber(importSummary.totalRows)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Rows
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="primary">
              {formatNumber(importSummary.uniqueSkus)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Unique SKUs
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="primary">
              {formatNumber(importSummary.totalQuantity)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Quantity
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="primary">
              {Object.keys(importSummary.warehouses).length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Warehouses
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Breakdown by warehouse */}
        <Typography variant="subtitle2" gutterBottom>
          By Warehouse
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Warehouse</TableCell>
                <TableCell align="right">Items</TableCell>
                <TableCell align="right">Total Quantity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(importSummary.warehouses).map(([code, data]) => (
                <TableRow key={code}>
                  <TableCell>
                    <Chip label={code} size="small" />
                  </TableCell>
                  <TableCell align="right">{formatNumber(data.count)}</TableCell>
                  <TableCell align="right">{formatNumber(data.quantity)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dates */}
        {importSummary.uniqueDates.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Count Dates
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {importSummary.uniqueDates.map(date => (
                <Chip
                  key={date}
                  icon={<DateIcon />}
                  label={formatDate(date)}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* What will happen */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          What Will Happen
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Physical count records will be created"
              secondary="One per warehouse/date combination"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Count items will be recorded"
              secondary="SKU quantities linked to each count"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Inventory adjustments will be calculated"
              secondary="Differences vs system stock create adjustment records"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><WarningIcon color="warning" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Stock levels will be updated"
              secondary="Adjustments affect current stock calculations"
            />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
}

export default ImportReviewStep;
